import { supabase } from '../config/supabase';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { isManualUploadedMedicalRecord, isMedicalRecordInvalidated } from './medicalRecordStatus';
import { MedicalRecordFileStorageService } from '../services/MedicalRecordFileStorageService';
import { getMedicalRecordAttachmentUrl } from './medicalRecordFiles';

export const updateUser = async (userId: string, userData: { userDisplayName: string }) => {
  const { data, error } = await supabase
    .from('users')
    .update({ 
      userDisplayName: userData.userDisplayName,
      userUpdatedAt: new Date().toISOString()
    })
    .eq('userId', userId);

  if (error) {
    console.error('Error updating user:', error);
    throw error;
  }

  return data;
};


export const getAllUsers = async () => {
  const { data, error } = await supabase
    .from('users')
    .select('*');

  if (error) {
    console.error('Error fetching users:', error);
    throw error;
  }

  return data;
};


interface MedicalRecordAttachment {
  name: string;
  url: string;
  path: string; // Adicionando o caminho do arquivo no storage
  type: string;
}

interface MedicalRecordData {
  patientId: string;
  doctorId: string;
  recordType: 'evaluation' | 'progress' | 'exam' | 'prescription' | 'report';
  recordDate: Date;
  recordTitle: string;
  recordDescription: string;
  recordAttachments: MedicalRecordAttachment[];
}

export const deleteMedicalRecord = async (recordId: string, clientId?: string): Promise<void> => {
  // Etapa 1: Obter o registro do prontuário para acessar os anexos
  let fetchQuery = supabase
    .from('medical_records')
    .select('recordAttachments')
    .eq('recordId', recordId);

  if (clientId) {
    fetchQuery = fetchQuery.eq('clientId', clientId);
  }

  const { data: record, error: fetchError } = await fetchQuery.single();

  if (fetchError || !record) {
    console.error('Error fetching medical record:', fetchError);
    throw new Error('Record not found');
  }

  // Etapa 2: Excluir anexos do Supabase Storage
  const attachments = record.recordAttachments as MedicalRecordAttachment[];
  if (attachments && attachments.length > 0) {
    const filePaths = attachments.map(att => att.path);
    const firebasePaths = filePaths.filter((path) => typeof path === 'string' && path.startsWith('tenants/'));
    const supabasePaths = filePaths.filter((path) => typeof path === 'string' && !path.startsWith('tenants/'));

    if (supabasePaths.length > 0) {
      const { error: storageError } = await supabase.storage
        .from('medical-records')
        .remove(supabasePaths);

      if (storageError) {
        console.error('Error deleting files from storage:', storageError);
      }
    }

    if (firebasePaths.length > 0) {
      await Promise.all(
        firebasePaths.map(async (path) => {
          try {
            await MedicalRecordFileStorageService.deleteFile(path);
          } catch (error) {
            console.error('Error deleting file from Firebase Storage:', error);
          }
        }),
      );
    }
  }

  // Etapa 3: Excluir o registro do prontuário
  let deleteQuery = supabase
    .from('medical_records')
    .delete()
    .eq('recordId', recordId);

  if (clientId) {
    deleteQuery = deleteQuery.eq('clientId', clientId);
  }

  const { error: deleteError } = await deleteQuery;

  if (deleteError) {
    console.error('Error deleting medical record:', deleteError);
    throw deleteError;
  }
};

export const getMedicalRecords = async (userId: string, userRole: string, clientId?: string) => {
  if (!clientId) {
    return [];
  }

  let query = supabase.from("medical_records").select(`
    recordId,
    recordTitle,
    recordType,
    recordDescription,
    recordAttachments,
    recordCreatedAt,
    clientId,
    patientId,
    doctorId,
    patient:patients (patientFullName),
    doctor:users (userDisplayName)
  `)
  .eq("clientId", clientId);

  if (userRole === "patient") {
    query = query.eq("patientId", userId);
  } else if (userRole === "doctor" || userRole === "fisioterapeuta") {
    query = query.eq("doctorId", userId);
  } else if (userRole !== "admin" && userRole !== "interno") {
    return [];
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching medical records:", error);
    throw error;
  }

  const records = data
    .filter((record: any) =>
      !isMedicalRecordInvalidated(record.recordType, record.recordAttachments) &&
      isManualUploadedMedicalRecord(record.recordAttachments)
    )
    .map((record: any) => ({
      id: record.recordId,
      name: record.recordTitle || "Untitled Document",
      url: getMedicalRecordAttachmentUrl(record.recordAttachments),
      uploadDate: new Date(record.recordCreatedAt),
      patientId: record.patientId,
      doctorId: record.doctorId,
      patientName: record.patient?.patientFullName || "Unknown Patient",
      doctorName: record.doctor?.userDisplayName || "Unknown Doctor",
      metadata: Array.isArray(record.recordAttachments)
        ? record.recordAttachments
        : {
            attachments: record.recordAttachments,
            type: record.recordType,
            description: record.recordDescription,
          },
    }));

  return records;
};

export const createMedicalRecord = async (medicalRecordData: Omit<MedicalRecordData, 'patientName' | 'doctorName'>): Promise<void> => {

  const { data, error } = await supabase
    .from('medical_records')
    .insert({
      patientId: medicalRecordData.patientId,
      doctorId: medicalRecordData.doctorId,
      recordType: medicalRecordData.recordType,
      recordDate: new Date(medicalRecordData.recordDate).toISOString(),
      recordTitle: medicalRecordData.recordTitle,
      recordDescription: medicalRecordData.recordDescription,
      recordAttachments: medicalRecordData.recordAttachments,
      // clientId deve ser obtido do contexto da aplicação, ou passado como parâmetro
      // clientId: 'SEU_CLIENT_ID'
    });

  if (error) {
    console.error("Error creating medical record:", error);
    throw error;
  }
};

// Funções de autenticação removidas para usar as de src/utils/auth.ts

export const updateUserRole = async (userId: string, clientId: string, newRole: 'patient' | 'doctor' | 'admin' | 'fisioterapeuta' | 'interno'): Promise<void> => {
  const { error } = await supabase
    .from('user_clients')
    .update({ userRole: newRole })
    .eq('userId', userId)
    .eq('clientId', clientId);

  if (error) {
    console.error('Error updating user role:', error);
    throw new Error('Erro ao atualizar o papel do usuário.');
  }
};
